import { processSearch } from './processSearch';

// Standardized search fields for different item types
export const ITEM_SEARCH_FIELDS = {
  INVENTORY: ['title', 'sku', 'description', 'brand'],
  ASSET: ['title', 'sku', 'description', 'brand', 'mName'],
  ORDER_ITEM: ['title', 'sku', 'description', 'brand', 'upcCode', 'website'],
} as const;

// Standardized search parameters
export const SEARCH_CONFIG = {
  MIN_SEARCH_LENGTH: 2,
  DEBOUNCE_DELAY: 300,
  DEFAULT_LIMIT: 50,
  MAX_LIMIT: 100,
} as const;

/**
 * Standardized item search function to ensure consistent search behavior
 * across inventory, order request, and item search screens
 */
export function standardizeItemSearch<T extends Record<string, any>>(
  items: T[],
  searchValue: string,
  searchFields: readonly string[],
): Array<{
  item: T;
  label: string;
  id: string;
  value: string;
}> {
  if (!searchValue || searchValue.length < SEARCH_CONFIG.MIN_SEARCH_LENGTH) {
    return [];
  }

  return items
    .map((item) => {
      const { result, searchBy } = processSearch(
        searchFields as string[],
        searchValue,
        item,
      );

      return {
        item,
        label: searchBy || item.title || item.sku || '',
        id: item.id,
        value: result || item.title || item.sku || '',
      };
    })
    .filter((item) => item.label && item.value); // Filter out items without proper search matches
}

/**
 * Get appropriate search fields based on context
 */
export function getSearchFieldsForContext(context: 'inventory' | 'asset' | 'order'): readonly string[] {
  switch (context) {
    case 'inventory':
      return ITEM_SEARCH_FIELDS.INVENTORY;
    case 'asset':
      return ITEM_SEARCH_FIELDS.ASSET;
    case 'order':
      return ITEM_SEARCH_FIELDS.ORDER_ITEM;
    default:
      return ITEM_SEARCH_FIELDS.INVENTORY;
  }
}

/**
 * Standardized query variables for item searches
 */
export function getStandardizedQueryVariables(
  searchValue: string,
  additionalFilters: Record<string, any> = {},
  limit = SEARCH_CONFIG.DEFAULT_LIMIT,
) {
  return {
    filters: {
      search: searchValue,
      ...additionalFilters,
    },
    sorts: [
      {
        sortField: 'sku',
        sortOrder: 'ASC' as const,
      },
      {
        sortField: 'title',
        sortOrder: 'ASC' as const,
      },
    ],
    limit: Math.min(limit, SEARCH_CONFIG.MAX_LIMIT),
  };
}
